/**
 * What Generate All CRFs tells the user when it comes back.
 *
 * On 2026-09-10 every one of 40 forms on a Clinical Dev study failed to save
 * its rendering - the Word-to-PDF converter refused the function - and the
 * page showed a GREEN "All 0 CRFs generated and saved." with the failures in a
 * second toast underneath. This module decides the messages instead, so a run
 * that saved nothing says so, once, with the reason.
 *
 * Pure - no React, no Firebase - so it can be tested from functions' vitest.
 */

/** Which step of saving a rendering failed. Mirrors the server's RenderFailureStage. */
export type RenderFailureStage = 'render' | 'converter' | 'storage' | 'record';

export interface RenderAllResult {
  rendered: number;
  failed: string[];
  /** Per-form failing step. Absent from a server deployed before 2026-09-10. */
  failures?: Array<{ formName: string; stage: RenderFailureStage }>;
}

export interface RenderAllMessage {
  type: 'success' | 'error' | 'warning' | 'info';
  text: string;
}

const STAGE_REASON: Record<RenderFailureStage, string> = {
  render: 'the form could not be laid out as a Word document',
  converter: 'the PDF converter did not return a PDF',
  storage: 'the PDF could not be stored',
  record: 'the form could not be updated to point at its PDF',
};

/** The commonest failing step, and how many forms it accounts for. */
function commonestStage(
  failures: RenderAllResult['failures'],
): { stage: RenderFailureStage; count: number } | null {
  if (!failures || failures.length === 0) return null;
  const counts = new Map<RenderFailureStage, number>();
  for (const f of failures) counts.set(f.stage, (counts.get(f.stage) ?? 0) + 1);
  let best: { stage: RenderFailureStage; count: number } | null = null;
  for (const [stage, count] of counts) {
    if (!best || count > best.count) best = { stage, count };
  }
  return best;
}

export function renderAllMessages(data: RenderAllResult | null | undefined): RenderAllMessage[] {
  const rendered = data?.rendered ?? 0;
  const failed = data?.failed ?? [];
  const total = rendered + failed.length;

  if (rendered === 0 && failed.length === 0) {
    return [{
      type: 'info',
      text: 'There were no approved forms to render, so nothing was saved. Forms uploaded with their own document keep that document.',
    }];
  }

  if (rendered === 0) {
    const top = commonestStage(data?.failures);
    const why = top
      ? top.count === failed.length
        ? `because ${STAGE_REASON[top.stage]}`
        : `mostly because ${STAGE_REASON[top.stage]} (${top.count} of ${failed.length})`
      : 'because the server could not save a rendering for any of them';
    return [{
      type: 'error',
      text: `Nothing was saved: none of the ${failed.length} CRFs could be rendered, ${why}. No form was changed. Please try again later, or report this if it keeps happening.`,
    }];
  }

  if (failed.length === 0) {
    return [{ type: 'success', text: `All ${rendered} CRFs generated and saved.` }];
  }

  return [
    { type: 'success', text: `${rendered} of ${total} CRFs generated and saved.` },
    {
      type: 'warning',
      text: `Could not save renderings for: ${failed.join(', ')}. Use Generate CRF on those forms.`,
    },
  ];
}
